import React, { useEffect, useRef, useState } from "react"
import {
  Box,
  Button,
  Flex,
  Heading,
  Input,
  Text,
  Textarea,
  Grid,
  Divider,
  RadioGroup,
  Radio,
  Stack,
  FormLabel,
  Drawer,
  DrawerBody,
  DrawerOverlay,
  DrawerContent,
} from "@chakra-ui/react"
import Icon from "@app/components/Icon"
import ProductGiftCardDenominations from "@app/components/Product/ProductGiftCardDenominations"
import { useValidation } from "@app/hooks/useValidation"
import { useCart } from "@app/hooks/useCart"
import { useAppContext } from "@app/providers/app"
import { DateInput } from "@app/components/DateInput"
import { useConfigContext } from "@app/providers/config"
import { useGiftcard } from "@app/hooks/useGiftcard"
import type { selectedOptions } from "@app/components/Product/ProductMain"
import type { Location } from "@root/types/global"

type Props = {
  product: any
  loading: boolean
  isOpen: boolean
  selectedOptions: selectedOptions
  onClose: () => void
  denominationValues: string[]
  selectedDenomination: string | undefined
  handleOption: (option: any) => void
  colours: string[]
  location: Location
}

const ProductGiftCardDrawer: React.FC<Props> = ({
  product,
  loading,
  isOpen,
  selectedOptions,
  onClose,
  denominationValues,
  selectedDenomination,
  handleOption,
  colours,
  location,
}) => {
  const { giftcardSettings } = useGiftcard(location)
  const { addToCart, loading: cartLoading } = useCart()
  const { activeVariant } = useAppContext()
  const { isEmail } = useValidation()
  const [denominationValueValid, setDenominationValueValid] = useState(true)
  const [recipientNameValid, setRecipientNameValid] = useState(true)
  const [senderNameValid, setSenderNameValidValid] = useState(true)
  const [recipientEmailValid, setRecipientEmailValid] = useState(true)
  const [recipientDateValid, setRecipientDateValid] = useState(true)
  const recipientNameRef = useRef<any>()
  const senderNameRef = useRef<any>()
  const recipientEmailRef = useRef<any>()
  const messageRef = useRef<any>()
  const [deliveryDate, setDeliveryDate] = useState<Date | string>(new Date())
  const [send, setSend] = useState("now")
  const {
    settings: { cart },
  } = useConfigContext()

  const today = new Date()
  today.setHours(0)
  today.setMinutes(0)
  const oneYearFromNow = new Date()
  oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1)

  const updateValidity = () => {
    const recipientName = recipientNameRef?.current?.value?.trim()
    const senderName = senderNameRef?.current?.value?.trim()
    const recipientEmail = recipientEmailRef?.current?.value?.trim()

    const denominationValid = denominationValues.includes(selectedDenomination || "")
    const recipientNameValid = recipientName.length > 0
    const senderNameValid = senderName.length > 0
    const recipientEmailValid = isEmail(recipientEmail)
    const recipientDateValid = deliveryDate >= today && deliveryDate <= oneYearFromNow

    setDenominationValueValid(denominationValid)
    setRecipientNameValid(recipientNameValid)
    setRecipientEmailValid(recipientEmailValid)
    setRecipientDateValid(recipientDateValid)
    setSenderNameValidValid(senderNameValid)

    return recipientNameValid && recipientEmailValid && recipientDateValid && denominationValid
  }

  const handleDateChange = (value: Date | string) => {
    if (value == "Invalid Date") {
      setDeliveryDate("")
    } else {
      setDeliveryDate(value)
    }
  }

  const onAddClick = () => {
    if (loading || cartLoading) {
      return
    }

    if (updateValidity()) {
      const date = deliveryDate ? new Date(deliveryDate) : new Date()
      const convertedDateAU = date.toLocaleDateString("en-AU", { year: "numeric", month: "2-digit", day: "2-digit" })
      addToCart(product, activeVariant, 1, [
        { key: cart.giftcardRecipient, value: recipientNameRef?.current?.value },
        { key: cart.giftSenderName, value: senderNameRef?.current?.value },
        { key: cart.giftRecipientEmail, value: recipientEmailRef?.current?.value },
        { key: cart.giftRecipientDeliveryDate, value: convertedDateAU },
        { key: cart.giftRecipientMessage, value: messageRef?.current?.value },
        {
          key: cart.giftRecipientItemRef,
          value: selectedOptions?.find((item: { name: string; value: string }) => item.name === "Design")?.value,
        },
        { key: "_timestamp", value: new Date().getTime().toString() }, // used to differentiate between identical giftcards
      ])
    }
  }

  useEffect(() => {
    if (!cartLoading) {
      onClose()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartLoading])

  return (
    <Drawer isOpen={isOpen} placement="right" size="lg" onClose={onClose}>
      <DrawerOverlay />
      <DrawerContent
        sx={{
          "&": {
            ...{ maxW: { base: "90vw !important", sm: "40rem !important" } },
          },
        }}
      >
        <DrawerBody bg="brand.white" p={0} overflow="auto" display="flex" flexDirection="column">
          {/* Sticky Header */}
          <Flex
            justifyContent="space-between"
            px={{ base: "5", sm: "14" }}
            position="sticky"
            top="0"
            pt="6"
            pb="4"
            bg="brand.white"
            zIndex={10}
          >
            {giftcardSettings?.drawerTitle && (
              <Heading variant="h2" fontSize="lg" lineHeight="shorter" fontWeight={700}>
                {giftcardSettings.drawerTitle}
              </Heading>
            )}
            <Box onClick={onClose} marginLeft="auto">
              <Icon name="closeSmall" width="4.5" height="4.5" cursor="pointer" />
            </Box>
          </Flex>

          {/* Main Scrolling Content */}
          <Box flex={1} overflowY="auto">
            <Box pb="15" px={{ base: "5", sm: "14" }}>
              <Heading variant="h3" fontSize="lg" lineHeight="5" mb="5" fontWeight={700}>
                Value
              </Heading>
              <Grid width="100%" rowGap="4" gap="3.5" templateColumns="repeat(3, 1fr)">
                <ProductGiftCardDenominations
                  denominationValues={denominationValues}
                  selectedDenomination={selectedDenomination}
                  handleOption={handleOption}
                  colours={colours}
                />
              </Grid>
              {!denominationValueValid && (
                <Text variant="text12" fontWeight={600} color="brand.red" mt="1">
                  Please choose a value.
                </Text>
              )}
              <Divider orientation="horizontal" my="5" />
              {giftcardSettings?.formTitle && (
                <Heading variant="h3" fontSize="lg" lineHeight="shorter" mb="5" fontWeight={700}>
                  {giftcardSettings?.formTitle}
                </Heading>
              )}

              <Box mb="5">
                <FormLabel lineHeight="shorter" fontSize="xs" mb="1">
                  {giftcardSettings?.senderLabel}
                </FormLabel>
                <Input ref={senderNameRef} variant="giftCardAlt" placeholder="Your Name" fontSize="md" />
                {!senderNameValid && (
                  <Text variant="text12" fontWeight={600} color="brand.red" mt="1">
                    Please enter a sender name.
                  </Text>
                )}
              </Box>

              <Box mb="5">
                <FormLabel lineHeight="shorter" fontSize="xs" mb="1">
                  {giftcardSettings?.recipientNameLabel}
                </FormLabel>
                <Input ref={recipientNameRef} variant="giftCardAlt" placeholder="Their Name" fontSize="md" />
                {!recipientNameValid && (
                  <Text variant="text12" fontWeight={600} color="brand.red" mt="1">
                    Please enter a recipient name.
                  </Text>
                )}
              </Box>

              <Box mb="5">
                <FormLabel lineHeight="shorter" fontSize="xs" mb="1">
                  {giftcardSettings?.recipientEmailLabel}
                </FormLabel>
                <Input ref={recipientEmailRef} variant="giftCardAlt" placeholder="Their Email" fontSize="md" />
                {!recipientEmailValid && (
                  <Text variant="text12" fontWeight={600} color="brand.red" mt="1">
                    Please enter a valid email.
                  </Text>
                )}
              </Box>
              <Box>
                <FormLabel lineHeight="shorter" fontSize="xs" mb="1">
                  {giftcardSettings?.messageLabel}
                </FormLabel>
                <Textarea ref={messageRef} variant="giftCardAlt" placeholder="Type a message" mb="5" fontSize="md" />
              </Box>

              <Flex justifyContent="space-between">
                <Heading variant="h5" fontSize="lg" lineHeight="shorter" mb="5" fontWeight={700}>
                  When do you want to send it?
                </Heading>

                <RadioGroup value={send} onChange={value => setSend(value)}>
                  <Stack spacing="2.5" direction="row">
                    <Radio colorScheme="customColor" value="now">
                      Now
                    </Radio>
                    <Radio colorScheme="customColor" value="later">
                      Later
                    </Radio>
                  </Stack>
                </RadioGroup>
              </Flex>

              {send === "later" && (
                <Box mb="5">
                  <DateInput
                    inline
                    description={product?.additionalGiftcardDateNote}
                    minDate={today}
                    maxDate={oneYearFromNow}
                    isDisabled={cartLoading}
                    onChange={handleDateChange}
                    name="_gift_recipient_delivery_date"
                    placeholder={product?.additionalGiftcardDate}
                    value={deliveryDate}
                    variant="giftCardAlt"
                    dateFormat={"dd/MM/yyyy"}
                  />
                </Box>
              )}

              {!recipientDateValid && (
                <Text variant="paragraph16" fontWeight={600} mb="5">
                  E-Gift Cards can only be sent out 1 year from todays date. Please select another date.
                </Text>
              )}
            </Box>
          </Box>

          {/* Sticky Footer Buttons */}
          <Flex gap="2" borderTop="1px solid" borderColor="primary.greyLight" padding="6">
            <Box flex={1}>
              <Button variant="fill" w="100%" onClick={onClose}>
                Cancel
              </Button>
            </Box>

            <Box flex={1}>
              <Button variant="fill" w="100%" onClick={onAddClick}>
                {cartLoading ? "Adding" : "Add to Bag"}
              </Button>
            </Box>
          </Flex>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  )
}

export default React.memo(ProductGiftCardDrawer)
